/**
 * `/a/<token>`: an artifact someone shared, outside the app and its gate — a
 * link shared with anyone needs no account. The page reads the link
 * (`GET /shared/:token`) and draws the one version it shows, in the same
 * frame the chat's panel uses, under the wordmark and its title. Nothing of
 * who shared it, their chat or their account is there to show.
 *
 * A banner says it is a user's page, not Mauvely's, and the tab title is
 * prefixed the same, so the maker's title never stands alone on Assist's
 * domain. Anyone can report it (`POST /shared/:token/report`).
 *
 * A link shared with named people or an organisation answers 401 to someone
 * signed out, and the page goes to sign in and comes back.
 *
 * "Remix in Assist" copies what it shows into the viewer's own Assist, in a
 * new chat (`POST /shared/:token/remix`), and opens it there. Signed out, it
 * goes to sign in first, and back here.
 */

import { useEffect, useState } from 'react';
import { ApiError, getShared, remixShared, reportShared, type SharedArtifact } from '../api';
import { ds } from '../ds';
import { ArtifactView } from '../components/ArtifactView';
import { Ph } from '../components/ui';

/** Prefixed onto the maker's own title, in the banner and the tab. */
const PREFIX = 'Shared page';

/** The "Made by a user" banner, with Report. */
function Banner({ token }: { token: string }) {
  const [state, setState] = useState<'idle' | 'asking' | 'sent'>('idle');
  const [reason, setReason] = useState('');
  const send = () => {
    setState('sent');
    void reportShared(token, reason.trim()).catch(() => undefined);
  };
  return (
    <div
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        padding: '7px 16px',
        background: 'var(--app-panel-alt, var(--app-panel))',
        borderBottom: '1px solid var(--border-hairline, var(--border-subtle))',
        fontSize: 12.5,
        color: 'var(--text-muted)'
      }}
    >
      <Ph name="info" size={14} />
      <span>Made by an Assist user, not by Mauvely.</span>
      <span style={{ flex: 1 }} />
      {state === 'sent' ? (
        <span style={{ color: 'var(--text-faint)' }}>Reported.</span>
      ) : state === 'asking' ? (
        <>
          <input
            aria-label="Why are you reporting this?"
            placeholder="What is wrong? (optional)"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            style={{
              flex: '1 1 180px',
              minWidth: 0,
              height: 28,
              padding: '0 8px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--app-panel-sunken, transparent)',
              color: 'var(--text-body)',
              font: 'inherit',
              fontSize: 12.5
            }}
          />
          <button type="button" className="bare" onClick={send} style={{ fontSize: 12.5, color: 'var(--text-link)' }}>
            Send
          </button>
        </>
      ) : (
        <button type="button" className="bare" onClick={() => setState('asking')} style={{ fontSize: 12.5, color: 'var(--text-link)' }}>
          Report
        </button>
      )}
    </div>
  );
}

export function SharedPage({ token }: { token: string }) {
  const { Wordmark, Button } = ds();
  const [shared, setShared] = useState<SharedArtifact | null>(null);
  const [problem, setProblem] = useState<{ message: string; retry: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [remixing, setRemixing] = useState(false);
  const [remixProblem, setRemixProblem] = useState<string | null>(null);

  const remix = () => {
    if (remixing) return;
    setRemixing(true);
    setRemixProblem(null);
    remixShared(token).then(
      (made) => {
        window.location.href = `/chat/${encodeURIComponent(made.chatId)}?artifact=${encodeURIComponent(made.artifactId)}`;
      },
      (err: unknown) => {
        const away = err instanceof ApiError ? (err.details.signInUrl ?? err.details.twoFactorUrl) : undefined;
        if (typeof away === 'string') {
          window.location.href = away;
          return;
        }
        setRemixing(false);
        setRemixProblem(err instanceof ApiError && err.status !== 0 && err.status < 500 ? err.message : 'That did not go through. Try again.');
      }
    );
  };

  useEffect(() => {
    let gone = false;
    setProblem(null);
    getShared(token).then(
      (a) => {
        if (gone) return;
        setShared(a);
        // The maker's title never stands alone on Assist's domain.
        document.title = `${PREFIX} · ${a.title}`;
      },
      (err: unknown) => {
        if (gone) return;
        const away = err instanceof ApiError ? (err.details.signInUrl ?? err.details.twoFactorUrl) : undefined;
        if (typeof away === 'string') {
          window.location.href = away;
          return;
        }
        const answered = err instanceof ApiError && err.status !== 0 && err.status < 500;
        setProblem({ message: answered ? err.message : 'Assist did not answer.', retry: !answered });
      }
    );
    return () => {
      gone = true;
    };
  }, [token, attempt]);

  return (
    <div
      className="mv-app"
      style={{
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--app-ground)',
        color: 'var(--text-body)',
        fontFamily: 'var(--font-body)'
      }}
    >
      <header style={{ flex: 'none', height: 52, display: 'flex', alignItems: 'center', gap: 14, padding: '0 16px', minWidth: 0 }}>
        <Wordmark lockup="ink" word="assist" badge="sparkle" size={15} />
        {shared ? (
          <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 14, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {shared.title}
          </span>
        ) : null}
        {shared ? (
          <Button variant="accent" density="app" size="sm" iconLeft="shuffle" disabled={remixing} onClick={remix}>
            Remix in Assist
          </Button>
        ) : null}
      </header>
      {remixProblem ? <div style={{ flex: 'none', padding: '0 16px 8px', fontSize: 13, color: 'var(--danger-600)' }}>{remixProblem}</div> : null}
      <Banner token={token} />
      <main
        style={{
          flex: 1,
          minHeight: 0,
          margin: '12px 16px 16px',
          borderRadius: 'var(--radius-region)',
          overflow: 'hidden',
          background: 'var(--app-panel)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {shared ? (
          <ArtifactView id={token} kind={shared.kind} content={shared.content} tokens={shared.tokens} title={shared.title} version={shared.version} unsafe={shared.unsafe} />
        ) : problem ? (
          <div style={{ margin: 'auto', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
            <span style={{ fontSize: 15, color: 'var(--text-strong)' }}>{problem.message}</span>
            {problem.retry ? (
              <Button variant="accent" density="app" size="sm" iconLeft="arrows-clockwise" onClick={() => setAttempt((n) => n + 1)}>
                Retry
              </Button>
            ) : null}
          </div>
        ) : (
          <div style={{ margin: 'auto' }}>
            <Ph name="circle-notch" size={22} className="spin" color="var(--text-faint)" />
          </div>
        )}
      </main>
    </div>
  );
}
